/**
 * Invoice Date Utilities
 *
 * Provides timezone-aware date handling for invoice dates.
 * All dates are stored in UTC (ISO format) but interpreted/displayed in the brand's timezone.
 * This matches the behavior of the Angular invoice implementation which uses moment.tz.setDefault().
 */

/**
 * Calculates timezone offset for a specific timezone and date
 */
export const getTimezoneOffsetInMinutes = (timezone: string, date: Date): number => {
    // Get the UTC time components
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    const hour = date.getUTCHours();
    const minute = date.getUTCMinutes();
    const second = date.getUTCSeconds();

    // Format this same moment in the target timezone
    const tzFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
    });

    const parts = tzFormatter.formatToParts(date);
    const tzYear = parseInt(parts.find((p) => p.type === 'year')?.value || '0');
    const tzMonth = parseInt(parts.find((p) => p.type === 'month')?.value || '0') - 1;
    const tzDay = parseInt(parts.find((p) => p.type === 'day')?.value || '0');
    const tzHour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0');
    const tzMinute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0');
    const tzSecond = parseInt(parts.find((p) => p.type === 'second')?.value || '0');

    // Calculate the difference
    // If UTC is 2025-12-23 06:00 and TZ shows 2025-12-23 00:00, offset is -360 (TZ is behind UTC)
    const utcMs = Date.UTC(year, month, day, hour, minute, second);
    const tzMs = Date.UTC(tzYear, tzMonth, tzDay, tzHour, tzMinute, tzSecond);

    return (tzMs - utcMs) / (1000 * 60);
};

/**
 * Converts a date input (YYYY-MM-DD) to UTC ISO string where the date represents start-of-day in brand timezone
 *
 * @example
 * convertDateInputToIso("2025-01-15", "America/Los_Angeles")
 * // Returns: "2025-01-15T08:00:00.000Z" (because LA is UTC-8)
 */
export const convertDateInputToIso = (
    dateString: string,
    brandTimezone: string
): Nullable<string> => {
    if (!dateString || !brandTimezone) {
        return null;
    }

    // Parse the date string (format: YYYY-MM-DD)
    const [year, month, day] = dateString.split('-').map(Number);
    // Create a date for the selected day at start-of-day UTC initially
    const targetDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
    // Calculate the timezone offset for this specific date
    const offsetMinutes = getTimezoneOffsetInMinutes(brandTimezone, targetDate);
    // Adjust the date: we want start-of-day in the brand timezone, not UTC
    // So we need to subtract the offset to get the correct UTC time
    targetDate.setUTCMinutes(targetDate.getUTCMinutes() - offsetMinutes);
    return targetDate.toISOString();
};

/**
 * Converts a JavaScript Date object to UTC ISO string where the date represents start-of-day in brand timezone
 * This is a convenience wrapper around convertDateInputToIso for Date objects from date pickers.
 *
 * @example
 * convertDateObjectToIso(new Date(2025, 0, 15), "America/Los_Angeles")
 * // Returns: "2025-01-15T08:00:00.000Z"
 */
export const convertDateObjectToIso = (date: Date, brandTimezone: string): Nullable<string> => {
    if (!date || !brandTimezone) {
        return null;
    }

    // Extract the date components (local to the user's browser)
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    // Use the existing conversion function
    return convertDateInputToIso(dateStr, brandTimezone);
};

/**
 * Converts UTC ISO string back to YYYY-MM-DD format in brand timezone for date picker display
 *
 * @example
 * convertIsoDateToInputFormat("2025-01-15T08:00:00.000Z", "America/Los_Angeles")
 * // Returns: "2025-01-15"
 */
export const convertIsoDateToInputFormat = (
    isoDateString: Nullable<string>,
    brandTimezone: string
): string => {
    if (!isoDateString || !brandTimezone) {
        return '';
    }

    // Parse the stored ISO date
    const storedDate = new Date(isoDateString);

    // Format the date in the brand timezone to get the original date
    const formatter = new Intl.DateTimeFormat('sv-SE', {
        timeZone: brandTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });

    return formatter.format(storedDate);
};

/**
 * Converts UTC ISO string to a Date object for use in date pickers
 * This is a convenience wrapper around convertIsoDateToInputFormat that returns a Date object.
 *
 * @example
 * convertIsoToDateObject("2025-01-15T08:00:00.000Z", "America/Los_Angeles")
 * // Returns: Date object representing Jan 15, 2025 (local to browser)
 */
export const convertIsoToDateObject = (
    isoDateString: Nullable<string>,
    brandTimezone: string
): Date | null => {
    if (!isoDateString || !brandTimezone) {
        return null;
    }

    // Convert ISO to YYYY-MM-DD in brand timezone
    const dateStr = convertIsoDateToInputFormat(isoDateString, brandTimezone);

    if (!dateStr) {
        return null;
    }

    // Parse and create Date object
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
};

/**
 * Gets today's date as YYYY-MM-DD string in the specified timezone
 */
export const getTodayInTimezone = (timezone: string): string => {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('sv-SE', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    });
    return formatter.format(now);
};

/**
 * Adds days to a UTC ISO string, performing the arithmetic in brand timezone
 * Returns a new UTC ISO string
 *
 * @example
 * addDaysInTimezone("2025-01-15T08:00:00.000Z", 30, "America/Los_Angeles")
 * // Returns: "2025-02-14T08:00:00.000Z"
 */
export const addDaysInTimezone = (
    isoString: string,
    days: number,
    brandTimezone: string
): string => {
    if (!isoString || !brandTimezone) {
        return isoString;
    }

    // Convert to YYYY-MM-DD in brand timezone
    const dateString = convertIsoDateToInputFormat(isoString, brandTimezone);

    if (!dateString) {
        return isoString;
    }

    // Parse the date
    const [year, month, day] = dateString.split('-').map(Number);
    // Create a date object and add days
    const date = new Date(year, month - 1, day);
    date.setDate(date.getDate() + days);

    // Convert back to YYYY-MM-DD
    const newYear = date.getFullYear();
    const newMonth = String(date.getMonth() + 1).padStart(2, '0');
    const newDay = String(date.getDate()).padStart(2, '0');
    const newDateString = `${newYear}-${newMonth}-${newDay}`;

    // Convert back to ISO using brand timezone
    return convertDateInputToIso(newDateString, brandTimezone) || isoString;
};

/**
 * Calculates day difference between two UTC ISO strings when interpreted in brand timezone
 *
 * @example
 * differenceInDaysInTimezone("2025-02-14T08:00:00.000Z", "2025-01-15T08:00:00.000Z", "America/Los_Angeles")
 * // Returns: 30
 */
export const differenceInDaysInTimezone = (
    isoString1: string,
    isoString2: string,
    brandTimezone: string
): number => {
    if (!isoString1 || !isoString2 || !brandTimezone) {
        return 0;
    }

    // Convert both to YYYY-MM-DD in brand timezone
    const dateString1 = convertIsoDateToInputFormat(isoString1, brandTimezone);
    const dateString2 = convertIsoDateToInputFormat(isoString2, brandTimezone);

    if (!dateString1 || !dateString2) {
        return 0;
    }

    // Parse dates
    const [year1, month1, day1] = dateString1.split('-').map(Number);
    const [year2, month2, day2] = dateString2.split('-').map(Number);

    const date1 = new Date(year1, month1 - 1, day1);
    const date2 = new Date(year2, month2 - 1, day2);

    // Calculate difference in milliseconds and convert to days
    const diffMs = date1.getTime() - date2.getTime();
    const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

    return diffDays;
};

/**
 * Ensures a UTC ISO string represents start-of-day (00:00:00) in brand timezone
 * Returns a new UTC ISO string
 */
export const startOfDayInTimezone = (isoString: string, brandTimezone: string): string => {
    if (!isoString || !brandTimezone) {
        return isoString;
    }

    // Convert to YYYY-MM-DD and back to ISO (which gives us start-of-day)
    const dateString = convertIsoDateToInputFormat(isoString, brandTimezone);

    if (!dateString) {
        return isoString;
    }

    return convertDateInputToIso(dateString, brandTimezone) || isoString;
};

/**
 * Compares if date1 is before date2 when both are interpreted in brand timezone
 * Compares at day granularity (ignores time)
 */
export const isBeforeInTimezone = (
    isoString1: string,
    isoString2: string,
    brandTimezone: string
): boolean => {
    if (!isoString1 || !isoString2 || !brandTimezone) {
        return false;
    }

    const dateString1 = convertIsoDateToInputFormat(isoString1, brandTimezone);
    const dateString2 = convertIsoDateToInputFormat(isoString2, brandTimezone);

    // Simple string comparison works for YYYY-MM-DD format
    return dateString1 < dateString2;
};

/**
 * Compares if date1 is after date2 when both are interpreted in brand timezone
 * Compares at day granularity (ignores time)
 */
export const isAfterInTimezone = (
    isoString1: string,
    isoString2: string,
    brandTimezone: string
): boolean => {
    if (!isoString1 || !isoString2 || !brandTimezone) {
        return false;
    }

    const dateString1 = convertIsoDateToInputFormat(isoString1, brandTimezone);
    const dateString2 = convertIsoDateToInputFormat(isoString2, brandTimezone);

    // Simple string comparison works for YYYY-MM-DD format
    return dateString1 > dateString2;
};

/**
 * Compares if two dates are the same day when interpreted in brand timezone
 */
export const isSameDayInTimezone = (
    isoString1: string,
    isoString2: string,
    brandTimezone: string
): boolean => {
    if (!isoString1 || !isoString2 || !brandTimezone) {
        return false;
    }

    const dateString1 = convertIsoDateToInputFormat(isoString1, brandTimezone);
    const dateString2 = convertIsoDateToInputFormat(isoString2, brandTimezone);

    return dateString1 === dateString2;
};

/**
 * Compares if date1 is less than or equal to date2 in brand timezone
 */
export const isBeforeOrEqualInTimezone = (
    isoString1: string,
    isoString2: string,
    brandTimezone: string
): boolean => {
    return (
        isBeforeInTimezone(isoString1, isoString2, brandTimezone) ||
        isSameDayInTimezone(isoString1, isoString2, brandTimezone)
    );
};

/**
 * Compares if date1 is greater than or equal to date2 in brand timezone
 */
export const isAfterOrEqualInTimezone = (
    isoString1: string,
    isoString2: string,
    brandTimezone: string
): boolean => {
    return (
        isAfterInTimezone(isoString1, isoString2, brandTimezone) ||
        isSameDayInTimezone(isoString1, isoString2, brandTimezone)
    );
};
